import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { graphBuilderApi } from '../../services/api';
import './DocumentConverter.css';

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const POLL_INTERVAL_MS = 3000;
const ACCEPTED_TYPES = ['.docx', '.pdf'];

/* ── Inline SVG icons ─────────────────────────────────────────────────────── */

const UploadIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" strokeLinecap="round" strokeLinejoin="round" />
    <polyline points="17 8 12 3 7 8" strokeLinecap="round" strokeLinejoin="round" />
    <line x1="12" y1="3" x2="12" y2="15" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const DownloadIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" strokeLinecap="round" strokeLinejoin="round" />
    <polyline points="7 10 12 15 17 10" strokeLinecap="round" strokeLinejoin="round" />
    <line x1="12" y1="15" x2="12" y2="3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <polyline points="9 6 15 12 9 18" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* ── Helpers ───────────────────────────────────────────────────────────────── */

function getFileExt(name) {
  return name.toLowerCase().slice(name.lastIndexOf('.') + 1);
}

function metricColor(value, greenThreshold, amberThreshold) {
  if (value >= greenThreshold) return 'green';
  if (value >= amberThreshold) return 'amber';
  return 'red';
}

function DocumentConverterPage() {
  const { graphId } = useParams();
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);
  const pollErrorCount = useRef(0);
  const MAX_POLL_ERRORS = 5;

  const [phase, setPhase] = useState('idle');
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [statusData, setStatusData] = useState(null);
  const [error, setError] = useState(null);
  const [showUncovered, setShowUncovered] = useState(false);
  const [showMissingTerms, setShowMissingTerms] = useState(false);
  const [showMissingItems, setShowMissingItems] = useState(false);

  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  /* ── File selection ────────────────────────────────────────────────────── */

  const validateFile = useCallback((f) => {
    const ext = f.name.toLowerCase().slice(f.name.lastIndexOf('.'));
    if (!ACCEPTED_TYPES.includes(ext)) {
      return `Only .docx and .pdf files are accepted. Got "${ext}".`;
    }
    if (f.size > MAX_FILE_SIZE) {
      return `File size (${(f.size / 1024 / 1024).toFixed(2)} MB) exceeds the 5 MB limit.`;
    }
    return null;
  }, []);

  const handleFileChosen = useCallback((f) => {
    setError(null);
    const err = validateFile(f);
    if (err) { setError(err); return; }
    setFile(f);
    setPhase('file_selected');
    setJobId(null);
    setStatusData(null);
  }, [validateFile]);

  const handleFileSelect = (e) => {
    const f = e.target.files?.[0];
    if (f) handleFileChosen(f);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFileChosen(f);
  };

  /* ── Process ───────────────────────────────────────────────────────────── */

  const handleProcess = async () => {
    if (!file) return;
    setError(null);
    setPhase('uploading');
    try {
      const result = await graphBuilderApi.preprocessUploadAsync(file, graphId);
      setJobId(result.job_id);
      setPhase('processing');
      startPolling(result.job_id);
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.detail || err.message || 'Upload failed.');
      setPhase('file_selected');
    }
  };

  /* ── Polling ───────────────────────────────────────────────────────────── */

  const startPolling = useCallback((jid) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollErrorCount.current = 0;
    pollRef.current = setInterval(async () => {
      try {
        const data = await graphBuilderApi.preprocessStatus(jid);
        pollErrorCount.current = 0;
        setStatusData(data);
        if (data.status === 'completed') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setPhase('processed');
        } else if (data.status === 'failed') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setPhase('failed');
          setError(data.error || 'Processing failed.');
        }
      } catch (err) {
        pollErrorCount.current += 1;
        console.error('Poll error:', err);
        if (pollErrorCount.current >= MAX_POLL_ERRORS) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setPhase('failed');
          setError('Lost connection to the server. Please try again.');
        }
      }
    }, POLL_INTERVAL_MS);
  }, []);

  /* ── Delete / restart ──────────────────────────────────────────────────── */

  const handleReset = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    setPhase('idle');
    setFile(null);
    setJobId(null);
    setStatusData(null);
    setError(null);
    setShowUncovered(false);
    setShowMissingTerms(false);
    setShowMissingItems(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDownloadSource = () => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name;
    a.click();
    URL.revokeObjectURL(url);
  };

  /* ── Display helpers ───────────────────────────────────────────────────── */

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const statusLabel = () => {
    switch (phase) {
      case 'file_selected': return 'Uploaded';
      case 'uploading':
      case 'processing': return 'Processing';
      case 'processed': return 'Processed';
      case 'failed': return 'Failed';
      default: return '';
    }
  };

  const statusBadgeClass = () => {
    switch (phase) {
      case 'file_selected': return 'doc-converter__badge--uploaded';
      case 'uploading':
      case 'processing': return 'doc-converter__badge--processing';
      case 'processed': return 'doc-converter__badge--processed';
      case 'failed': return 'doc-converter__badge--failed';
      default: return '';
    }
  };

  const isProcessing = phase === 'uploading' || phase === 'processing';
  const validation = statusData?.validation;
  const isValid = validation?.is_valid;
  const fileExt = file ? getFileExt(file.name) : '';

  /* ── Render ────────────────────────────────────────────────────────────── */

  return (
    <div className="doc-converter">
      <div className="doc-converter__header">
        <h2>Document Converter</h2>
        <p>
          Upload a source document (.docx or .pdf, max 5 MB) to restructure it
          for optimal graph building. The converted document can then be uploaded
          to the graph builder.
        </p>
      </div>

      {/* Upload zone */}
      {phase === 'idle' && (
        <div
          className={`doc-converter__dropzone ${dragActive ? 'doc-converter__dropzone--active' : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_TYPES.join(',')}
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          <div className="doc-converter__dropzone-icon">
            <UploadIcon />
          </div>
          <p className="doc-converter__dropzone-text">
            <strong>Click to upload</strong> or drag and drop
          </p>
          <p className="doc-converter__dropzone-hint">
            PDF or DOCX — max 5 MB
          </p>
        </div>
      )}

      {/* Document card */}
      {phase !== 'idle' && file && (
        <div className="doc-converter__card">
          <div className="doc-converter__card-row">

            {/* Source document */}
            <div className="doc-converter__cell">
              <p className="doc-converter__cell-label">Source Document</p>
              <div className="doc-converter__cell-filename">
                <span className={`doc-converter__file-icon doc-converter__file-icon--${fileExt}`}>
                  {fileExt}
                </span>
                {file.name}
              </div>
              <p className="doc-converter__cell-meta">{formatSize(file.size)}</p>
              <span className={`doc-converter__badge ${statusBadgeClass()}`}>
                {isProcessing && <span className="doc-converter__spinner" />}
                {statusLabel()}
              </span>
              <button
                className="doc-converter__download-link"
                onClick={handleDownloadSource}
                type="button"
              >
                <DownloadIcon /> Download source
              </button>
            </div>

            {/* Converted document */}
            {phase === 'processed' && jobId ? (
              <div className="doc-converter__cell doc-converter__cell--converted">
                <p className="doc-converter__cell-label">Converted Document</p>
                <div className="doc-converter__cell-filename">
                  <span
                    className={`doc-converter__valid-icon ${isValid ? 'doc-converter__valid-icon--pass' : 'doc-converter__valid-icon--warn'}`}
                    title={isValid ? 'All source information captured' : 'Some information may be missing'}
                  >
                    {isValid ? '✓' : '!'}
                  </span>
                  restructured_{jobId}.docx
                </div>

                {/* Validation metrics */}
                {validation && (
                  <div className="doc-converter__metrics">
                    {validation.sentences_covered != null && (
                      <div className="doc-converter__metric-row" title="Fraction of source sentences whose content words appear in the output">
                        <span className={`doc-converter__metric-dot doc-converter__metric-dot--${metricColor(validation.sentence_coverage ?? 0, 0.80, 0.60)}`} />
                        <span className="doc-converter__metric-label">Sentences covered</span>
                        <span className="doc-converter__metric-value">
                          {validation.sentences_covered}/{validation.sentences_total} ({((validation.sentence_coverage ?? 0) * 100).toFixed(1)}%)
                        </span>
                      </div>
                    )}

                    {validation.domain_term_coverage != null && (
                      <div className="doc-converter__metric-row" title="Domain-specific terms, acronyms, and numbers preserved verbatim">
                        <span className={`doc-converter__metric-dot doc-converter__metric-dot--${metricColor(validation.domain_term_coverage, 0.70, 0.50)}`} />
                        <span className="doc-converter__metric-label">Domain terms</span>
                        <span className="doc-converter__metric-value">
                          {(validation.domain_term_coverage * 100).toFixed(1)}%
                        </span>
                      </div>
                    )}

                    {validation.llm_missing_items?.length > 0 && (
                      <div className="doc-converter__metric-row" title="Facts identified as missing by the LLM fact audit">
                        <span className="doc-converter__metric-dot doc-converter__metric-dot--amber" />
                        <span className="doc-converter__metric-label">Missing items (fact audit)</span>
                        <span className="doc-converter__metric-value">
                          {validation.llm_missing_items.length}
                        </span>
                      </div>
                    )}

                    {(validation.uncovered_sentence_count ?? 0) > 0 && (
                      <div className="doc-converter__metric-row" title="Source sentences that may not be fully represented in the output">
                        <span className="doc-converter__metric-dot doc-converter__metric-dot--amber" />
                        <span className="doc-converter__metric-label">Sentences to review</span>
                        <span className="doc-converter__metric-value">
                          {validation.uncovered_sentence_count}
                        </span>
                      </div>
                    )}

                    {/* Fallback for older responses without sentence_coverage */}
                    {validation.sentences_covered == null && validation.keyword_coverage != null && (
                      <div className="doc-converter__metric-row">
                        <span className={`doc-converter__metric-dot doc-converter__metric-dot--${metricColor(validation.stemmed_coverage ?? validation.keyword_coverage, 0.80, 0.60)}`} />
                        <span className="doc-converter__metric-label">Keyword coverage</span>
                        <span className="doc-converter__metric-value">
                          {((validation.stemmed_coverage ?? validation.keyword_coverage) * 100).toFixed(1)}%
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* ── Review panels (collapsible) ────────────────────── */}

                {validation?.uncovered_sentences?.length > 0 && (
                  <div className="doc-converter__review-panel">
                    <button
                      className="doc-converter__review-header"
                      onClick={() => setShowUncovered(v => !v)}
                      type="button"
                    >
                      <span className={`doc-converter__review-chevron ${showUncovered ? 'doc-converter__review-chevron--open' : ''}`}>
                        <ChevronIcon />
                      </span>
                      Review uncovered sentences ({validation.uncovered_sentence_count ?? validation.uncovered_sentences.length})
                    </button>
                    {showUncovered && (
                      <div className="doc-converter__review-body">
                        <ol className="doc-converter__review-list">
                          {validation.uncovered_sentences.map((s, i) => (
                            <li key={i} className="doc-converter__review-item">
                              <span className="doc-converter__review-item-number">{i + 1}.</span>
                              {s}
                            </li>
                          ))}
                        </ol>
                        {(validation.uncovered_sentence_count ?? 0) > validation.uncovered_sentences.length && (
                          <p className="doc-converter__review-note">
                            Showing {validation.uncovered_sentences.length} of {validation.uncovered_sentence_count} — remaining sentences were not returned by the API.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {validation?.missing_domain_terms?.length > 0 && (
                  <div className="doc-converter__review-panel">
                    <button
                      className="doc-converter__review-header"
                      onClick={() => setShowMissingTerms(v => !v)}
                      type="button"
                    >
                      <span className={`doc-converter__review-chevron ${showMissingTerms ? 'doc-converter__review-chevron--open' : ''}`}>
                        <ChevronIcon />
                      </span>
                      Missing domain terms ({validation.missing_domain_terms.length})
                    </button>
                    {showMissingTerms && (
                      <div className="doc-converter__review-body">
                        <div className="doc-converter__review-tags">
                          {validation.missing_domain_terms.map((t, i) => (
                            <span key={i} className="doc-converter__review-tag">{t}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {validation?.llm_missing_items?.length > 0 && (
                  <div className="doc-converter__review-panel">
                    <button
                      className="doc-converter__review-header"
                      onClick={() => setShowMissingItems(v => !v)}
                      type="button"
                    >
                      <span className={`doc-converter__review-chevron ${showMissingItems ? 'doc-converter__review-chevron--open' : ''}`}>
                        <ChevronIcon />
                      </span>
                      Missing items — fact audit ({validation.llm_missing_items.length})
                    </button>
                    {showMissingItems && (
                      <div className="doc-converter__review-body">
                        {validation.llm_missing_items.map((item, i) => (
                          <div key={i} className="doc-converter__review-bullet">{item}</div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {statusData?.word_count && (
                  <p className="doc-converter__cell-meta">
                    {statusData.word_count.toLocaleString()} words · {statusData.table_count || 0} table(s)
                  </p>
                )}

                <a
                  className="doc-converter__download-link"
                  href={graphBuilderApi.preprocessDownloadUrl(jobId)}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <DownloadIcon /> Download converted document
                </a>
              </div>
            ) : (
              <div className="doc-converter__cell doc-converter__cell--empty">
                {isProcessing
                  ? <>
                      <span className="doc-converter__spinner" />
                      <span>Converting document...</span>
                    </>
                  : 'Converted document will appear here'}
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div className="doc-converter__actions">
            {phase === 'file_selected' && (
              <button
                className="doc-converter__btn doc-converter__btn--primary"
                onClick={handleProcess}
                disabled={isProcessing}
              >
                Process Document
              </button>
            )}

            {phase === 'failed' && (
              <button
                className="doc-converter__btn doc-converter__btn--primary"
                onClick={handleProcess}
                disabled={isProcessing}
              >
                Retry Processing
              </button>
            )}

            <button
              className="doc-converter__btn doc-converter__btn--danger"
              onClick={handleReset}
              disabled={isProcessing}
            >
              Delete &amp; Restart
            </button>
          </div>
        </div>
      )}

      {/* Error display */}
      {error && (
        <div className="doc-converter__error">
          {error}
        </div>
      )}
    </div>
  );
}

export default DocumentConverterPage;
